/**
 * A navigation tabs that support infinite smooth scrolling
 *
 * Event:
 *  - change: Return new tabIndex and existing tabIndex
 * Methods:
 *  - selectTab({Number} tabIndex)
 *
 */

<template>
  <div
    class="navigation-tabs-smooth__tabs"
    @touchmove="$_handleDefaultTouchMove"
  >
    <div
      ref="scroller"
      class="navigation-tabs-smooth__scroller"
      @click="$_handleClick"
    >
      <slot />
    </div>
  </div>
</template>

<script>
import IScroll from 'iscroll';
import debounce from 'lodash/debounce';

export default {
  props: {
    initialIndex: {
      type: Number,
      default: 0,
    },
    handleSelection: {
      type: Boolean,
      default: true,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      activeIndex: this.initialIndex,
      activeTab: null,
    };
  },
  created() {
    this.$_smoothScroll_initialiseDebounced = debounce(this.$_smoothScroll_initialise, 200);
    this.myScroll = null;
    this.touchInputs = {};
  },
  mounted() {
    window.addEventListener('resize', this.$_smoothScroll_initialiseDebounced);
    this.$_smoothScroll_initialise();
    this.selectTab(this.activeIndex);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.$_smoothScroll_initialiseDebounced);
    this.$_smoothScroll_destroy();
  },
  methods: {
    selectTab(requestIndex) {
      // Get selection DOM elements
      const nextChild = this.$refs.scroller.children[requestIndex];
      const prevChild = this.activeTab;

      if (nextChild !== prevChild) {
        if (this.handleSelection) {
          // Attempt to modify DOM class
          prevChild && prevChild.classList.remove('selected');
          nextChild && nextChild.classList.add('selected');
        }

        // Notify change
        this.$_notifyChange(requestIndex, this.activeIndex);

        // Update vue state;
        this.activeIndex = requestIndex;
        this.activeTab = nextChild;
      }

      // Check if element is currently visible
      this.$_revealTabIfHidden();
    },

    $_notifyChange(newVal, oldVal) {
      this.$emit('change', {
        index: newVal,
        previousIndex: oldVal,
      });
    },

    $_revealTabIfHidden() {
      const selectedTab = this.activeTab;

      if (selectedTab) {
        const scroller = this.$el;

        const selectedRect = selectedTab.getBoundingClientRect();
        const parentRect = scroller.getBoundingClientRect();

        const isVisible = (
          selectedRect.left >= parentRect.left &&
          selectedRect.right <= parentRect.right
        );
        if (!isVisible) {
          // Scroll item into view
          const proposedScrollPosition = -(selectedTab.offsetLeft - selectedTab.offsetWidth/2);
          const scrollPosition = (proposedScrollPosition < this.myScroll.maxScrollX)
            ? this.myScroll.maxScrollX
            : proposedScrollPosition;
          this.myScroll.scrollTo(scrollPosition, 0);
        }
      }
    },

    $_smoothScroll_initialise() {
      this.$refs.scroller.style.width = this.$refs.scroller.scrollWidth + 'px';
      const scrollBar = this.$el;

      // Initialise smooth horizontal scroller
      this.myScroll = new IScroll(scrollBar, {
        mouseWheel: true,
        scrollbars: false,
        scrollX: true,
        scrollY: false,
        click: true,
        tap: true,
        ...this.options,
      });
    },
    $_smoothScroll_destroy() {
      if (this.myScroll) {
        this.myScroll.destroy();
        this.myScroll = null;
      }
    },

    $_handleDefaultTouchMove(event) {
      [...event.touches].forEach((touch) => {
        // Get and set
        const prevTouch = { ...(this.touchInputs[touch.identifier] || {}) };
        this.touchInputs[touch.identifier] = { x: touch.clientX, y: touch.clientY, timeStamp: event.timeStamp };
        const newTouch = this.touchInputs[touch.identifier];

        if (!prevTouch.timeStamp || ((newTouch.timeStamp - prevTouch.timeStamp) > 800)) {
          // is new input
          event.preventDefault();
        } else {
          const diffX = Math.abs(newTouch.x - prevTouch.x);
          const diffY = Math.abs(newTouch.y - prevTouch.y);
          if (diffX > diffY) {
            event.preventDefault();
          }
        }
      });
    },

    $_handleClick(event) {
      // Find tab DOM
      let node = event.target;
      while (node && node.parentNode !== this.$refs.scroller) {
        node = node.parentNode;
      }
      if (!node) return;

      // Find this element position in container
      let expectedElementIndex = 0;
      while (node = node.previousElementSibling) {
        expectedElementIndex++;
      }
      this.selectTab(expectedElementIndex);
    },
  },
};
</script>

<style lang="scss" scoped>
.navigation-tabs-smooth__tabs {
  width: 100%;
  overflow: hidden;
  position: relative;
}
.navigation-tabs-smooth__scroller {
  position: absolute;
  display: flex;
  flex-wrap: nowrap;
}
.navigation-tabs-smooth__scroller > * {
  flex-shrink: 0;
}
</style>
